import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { ApiError, paymentsApi } from '@/lib/pillarApi';
import type { CustomerPortalDashboardResponse } from '@anchorbase/pillar-sdk';
import { formatPaymentRequestSourceDocumentReference } from '@/app/payment-requests/source-document-reference';

function formatCurrency(amount: number | undefined, currencyCode: string | undefined): string {
    if (amount === undefined || currencyCode === undefined) {
        return '—';
    }
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
        }).format(amount);
    } catch {
        return `${amount.toFixed(2)} ${currencyCode}`;
    }
}

function formatDateTime(value: string | undefined): string {
    if (value === undefined) {
        return '—';
    }
    return new Date(value).toLocaleString();
}

export function CustomerDashboardPageContent() {
    const [selectedMerchantOrganizationUuid, setSelectedMerchantOrganizationUuid] = useState<
        string | undefined
    >(undefined);

    const dashboardQuery = useQuery<CustomerPortalDashboardResponse>({
        queryKey: ['customerPortalDashboard', selectedMerchantOrganizationUuid],
        queryFn: async () =>
            await paymentsApi.getCustomerPortalDashboard({
                merchant_organization_uuid: selectedMerchantOrganizationUuid,
            }),
        retry: false,
    });

    if (dashboardQuery.isLoading) {
        return <div>Loading customer dashboard...</div>;
    }

    if (dashboardQuery.error !== null || dashboardQuery.data === undefined) {
        const errorMessage =
            dashboardQuery.error instanceof ApiError
                ? dashboardQuery.error.message
                : 'Failed to load customer dashboard.';

        return (
            <Alert variant="destructive">
                <AlertTitle>Unable to load dashboard</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
        );
    }

    const dashboard = dashboardQuery.data;
    const selectedValue =
        selectedMerchantOrganizationUuid ??
        dashboard.selected_merchant_organization_uuid ??
        'all';

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                    <h1 className="text-3xl font-bold">Customer Dashboard</h1>
                    <p className="text-sm text-muted-foreground">
                        Review open balances and recent payment requests.
                    </p>
                </div>
                <Select
                    value={selectedValue}
                    onValueChange={(value) => {
                        setSelectedMerchantOrganizationUuid(
                            value === 'all' ? undefined : value
                        );
                    }}
                >
                    <SelectTrigger className="w-[280px]">
                        <SelectValue placeholder="Filter by merchant" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All merchants</SelectItem>
                        {dashboard.merchant_options.map((merchantOption) => (
                            <SelectItem
                                key={merchantOption.merchant_organization_uuid}
                                value={merchantOption.merchant_organization_uuid}
                            >
                                {merchantOption.merchant_display_name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
                <Card>
                    <CardHeader>
                        <CardTitle>Open Requests</CardTitle>
                    </CardHeader>
                    <CardContent className="text-3xl font-semibold">
                        {dashboard.summary.open_payment_request_count}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle>Overdue Requests</CardTitle>
                    </CardHeader>
                    <CardContent className="text-3xl font-semibold">
                        {dashboard.summary.overdue_payment_request_count}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle>Open Balance</CardTitle>
                    </CardHeader>
                    <CardContent className="text-3xl font-semibold">
                        {formatCurrency(
                            dashboard.summary.open_payment_request_total_amount,
                            dashboard.summary.currency_code
                        )}
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Recent Payment Requests</CardTitle>
                </CardHeader>
                <CardContent>
                    {dashboard.recent_payment_requests.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            No payment requests are available for this customer.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {dashboard.recent_payment_requests.map((paymentRequest) => {
                                const formattedSourceDocumentReference =
                                    formatPaymentRequestSourceDocumentReference({
                                        sourceDocumentReference:
                                            paymentRequest.source_document_reference,
                                        sourceDocumentKey: paymentRequest.source_document_key,
                                    });

                                return (
                                    <div
                                        key={paymentRequest.payment_request_uuid}
                                        className="rounded-lg border p-4"
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-semibold">
                                                        {paymentRequest.title ??
                                                            formattedSourceDocumentReference ??
                                                            'Payment Request'}
                                                    </h3>
                                                    <StatusBadge status={paymentRequest.status} />
                                                </div>
                                                <p className="text-sm text-muted-foreground">
                                                    Reference:{' '}
                                                    {formattedSourceDocumentReference ?? '—'}
                                                </p>
                                            </div>
                                            <div className="text-right text-sm">
                                                <p className="font-semibold">
                                                    {formatCurrency(
                                                        paymentRequest.original_amount,
                                                        paymentRequest.currency_code
                                                    )}
                                                </p>
                                                <p className="text-muted-foreground">
                                                    Created:{' '}
                                                    {formatDateTime(paymentRequest.created_ts)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export default function CustomerDashboardPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <CustomerDashboardPageContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
